import { format } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

// All slot/booking times are Europe/Berlin wall-clock times, independent of
// the visitor's browser timezone. Cloud Functions use the same convention.
export const WAREHOUSE_TIMEZONE = 'Europe/Berlin';

// Accepts Date | ISO string | epoch ms | Firestore Timestamp(-like) and
// returns a Date (or null when the value cannot be interpreted).
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
  }
  return null;
}

// Formats an instant using the warehouse (Europe/Berlin) wall clock.
export function formatWarehouse(value, fmt = 'yyyy-MM-dd HH:mm:ss') {
  const date = toDate(value);
  return date && !Number.isNaN(date.getTime())
    ? formatInTimeZone(date, WAREHOUSE_TIMEZONE, fmt)
    : '';
}

// Lock/slot key (`YYYY-MM-DD_HH-mm`) identical to the backend
// getReservationSlotKey() implementation.
export function warehouseSlotKey(value) {
  return formatWarehouse(value, 'yyyy-MM-dd_HH-mm');
}

export function warehouseDateKey(value) {
  return formatWarehouse(value, 'yyyy-MM-dd');
}

export function warehouseMonthKey(value) {
  return formatWarehouse(value, 'yyyy-MM');
}

// Value for <input type="datetime-local"> that shows the warehouse wall clock.
export function warehouseInputValue(value) {
  return formatWarehouse(value, "yyyy-MM-dd'T'HH:mm");
}

// "Pseudo" Date: its LOCAL wall-clock fields equal the current Europe/Berlin
// wall clock. The scheduler generates slots from local components, so working
// in this pseudo space keeps day/hour semantics identical for every visitor.
export function warehouseNow() {
  return toZonedTime(new Date(), WAREHOUSE_TIMEZONE);
}

// Same pseudo conversion for an arbitrary instant (e.g. the stored firstDate
// ISO string -> a Date whose local fields equal the Berlin wall clock).
export function warehousePseudoDate(value) {
  const date = toDate(value);
  return date ? toZonedTime(date, WAREHOUSE_TIMEZONE) : null;
}

// Converts a wall-clock string (e.g. from a datetime-local input) that was
// written in the warehouse timezone into the real instant.
export function parseWarehouseInput(value) {
  return fromZonedTime(value, WAREHOUSE_TIMEZONE);
}

// Parses a date/time value: strings WITHOUT an explicit timezone suffix are
// treated as warehouse (Europe/Berlin) wall-clock; strings carrying `Z` or an
// explicit offset describe the instant directly. Mirrors the backend's
// getReservationSlotKey()/getReservationDateKey() handling.
export function parseWarehouseDateTime(value) {
  if (typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
    return fromZonedTime(value, WAREHOUSE_TIMEZONE);
  }
  return toDate(value);
}

// Local formatting for pseudo Dates (their local components already equal the
// warehouse wall clock).
export function formatPseudo(date, fmt = 'yyyy-MM-dd HH:mm:ss') {
  return format(date, fmt);
}
